import { IconAlertTriangle, IconBan, IconDots, IconExternalLink, IconPlayerPlayFilled, IconSquareRoundedX } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { TERMINAL_NEGATIVOS } from "@/components/solicitudes/stepper/steps-config";
import {
  Alert,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Group,
  IconAction,
  IconButton,
  Stack,
  Td,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Tr,
  useConfirmDialog,
} from "@/components/ui";
import { TONO_HEX, tonoDeEstado } from "@/lib/estado-color";
import { MotivosForm } from "@/features/autorizacion/components/MotivosForm";
import { useDarDeBaja } from "@/features/autorizacion/hooks/use-autorizacion-mutations";
import { useCancelarSolicitud } from "@/features/solicitudes/hooks/use-cancelar-solicitud";
import { ESTADOS_BAJA_POR_MESA_CONTROL, ESTADOS_CANCELABLES_POR_SOLICITANTE } from "@/features/solicitud-detalle/utils/estado-flags";
import { useAuthStore } from "@/lib/auth-store";
import { ESTADOS_EN_AUTORIZACION, etiquetaEstado } from "@/lib/estado-labels";
import type { MiSolicitud } from "@/lib/types";
import { formatFecha } from "@/shared/format/fecha";
import { formatearMonto } from "@/shared/format/money";

/** Etiqueta amigable del tipo de solicitud · el enum crudo va pegado
 *  ("ContratoAnualizado") y se muestra con espacios. */
function etiquetaTipo(tipo: string | null | undefined): string {
  if (!tipo) return "—";
  if (tipo === "ContratoAnualizado") return "Contrato Anualizado";
  if (tipo === "OrdenDeCompra") return "Orden de Compra";
  return tipo;
}

/** Etiqueta amigable del subtipo · el enum crudo va con guion bajo
 *  ("CFDI_UNICO", "MULTIPLES_CFDI") y se muestra con espacios (y acento). */
function etiquetaSubtipo(subtipo: string | null | undefined): string {
  if (!subtipo) return "—";
  if (subtipo === "CFDI_UNICO") return "CFDI ÚNICO";
  return subtipo.replace(/_/g, " ");
}

/**
 * URL del paso donde el solicitante debe *continuar* la solicitud según su
 * estado actual (pre-envío). Devuelve `null` para estados ya enviados o
 * terminales, donde no hay nada que continuar. Path concreto (id sustituido).
 */
function pathContinuar(s: MiSolicitud): string | null {
  const base = `/solicitudes/${s.id}`;
  const esMultiple = s.subtipo === "MULTIPLES_CFDI";
  const esCA = s.tipo === "ContratoAnualizado";
  const esOC = s.tipo === "OrdenDeCompra";
  switch (s.estado) {
    case "SubtipoSeleccionado":
      if (esCA) return `${base}/contrato-anualizado`;
      if (esOC) return `${base}/orden-compra`;
      return esMultiple ? `${base}/cargar-zip` : `${base}/cargar-cfdi`;
    case "ContratoAnualizadoSeleccionado":
      return `${base}/cargar-cfdi`;
    case "OrdenDeCompraSeleccionada":
      return `${base}/cargar-cfdi`;
    case "Iniciada":
    case "TipoSeleccionado":
    case "CfdiPendiente":
    case "CfdiCargado":
    case "EnValidacionReglas":
    case "ReglasRechazadas":
    case "EnValidacionFiscal":
    case "RechazadoFiscal":
    case "SuspendidaFiscal":
      return esMultiple ? `${base}/cargar-zip` : `${base}/cargar-cfdi`;
    case "Validado":
    case "CapturandoDatos":
      return `${base}/datos-generales`;
    case "SeleccionandoProveedor":
      return `${base}/proveedor`;
    case "Prorrateando":
      return esMultiple ? `${base}/prorrateo-multiple` : `${base}/prorrateo`;
    case "ProvisionalmenteListo":
    case "AdjuntandoDocumentos":
      return `${base}/documentos`;
    default:
      return null;
  }
}

/** `n/m` del flujo de autorización cuando aporta (ni terminal negativo ni ya Autorizada). */
function nivelFlujo(s: MiSolicitud): string | null {
  if (!s.flujoNivelActual || !s.flujoCantidadNiveles) return null;
  if (s.estado === "Autorizada" || TERMINAL_NEGATIVOS.has(s.estado)) return null;
  return `${s.flujoNivelActual}/${s.flujoCantidadNiveles}`;
}

/**
 * Overrides SOLO para el badge compacto de la tabla · el nombre completo
 * del estado sigue viviendo en el tooltip (`tooltipEstado`).
 */
const LABEL_COMPACTO: Record<string, string> = {
  ReglasRechazadas: "Rechazada",
};

/**
 * Etiqueta compacta para el badge de la tabla · en autorización el nivel
 * sustituye al ordinal verboso ("En autorización primer nivel" →
 * "Autorización 1/3"); en el resto, el contador se anexa al nombre.
 */
function etiquetaEstadoCompacta(s: MiSolicitud): string {
  const nivel = nivelFlujo(s);
  const base = LABEL_COMPACTO[s.estado];
  if (!nivel) return base ?? etiquetaEstado(s.estado, { nivelActual: s.flujoNivelActual, rolActual: s.flujoRolActual });
  if (ESTADOS_EN_AUTORIZACION.has(s.estado)) return `Autorización ${nivel}`;
  return `${base ?? etiquetaEstado(s.estado)} · ${nivel}`;
}

/** Tooltip del badge · nombre completo del estado + nivel explícito. */
function tooltipEstado(s: MiSolicitud): string {
  const completa = etiquetaEstado(s.estado, { nivelActual: s.flujoNivelActual, rolActual: s.flujoRolActual });
  const nivel = nivelFlujo(s);
  return nivel ? `${completa} · nivel ${s.flujoNivelActual} de ${s.flujoCantidadNiveles}` : completa;
}

/** Fila de la tabla desktop · presentacional. */
export function SolicitudRow({ s }: { s: MiSolicitud }) {
  const navigate = useNavigate();
  const user = useAuthStore((st) => st.user);
  const cancelar = useCancelarSolicitud(s.id);
  const baja = useDarDeBaja(s.id, { onBaja: () => setBajaOpen(false) });
  const [bajaOpen, setBajaOpen] = useState(false);
  const confirm = useConfirmDialog();

  const irADetalle = () => navigate({ to: "/solicitudes/$id", params: { id: s.id } });

  const esSolicitante = user?.roles.includes("Solicitante") ?? false;
  const esMesaDeControl = user?.roles.includes("MesaDeControl") ?? false;
  // "Continuar solicitud" navega a un paso del wizard exclusivo del dueño de la
  // solicitud (ej. prorrateo) · Mesa de Control ve las solicitudes de TODOS los
  // solicitantes (decisión P29), así que mostrárselo aquí llevaba a un 403 del
  // backend (esos endpoints son solo para el solicitante dueño).
  const urlContinuar = esSolicitante ? pathContinuar(s) : null;
  const puedeCancelar = esSolicitante && ESTADOS_CANCELABLES_POR_SOLICITANTE.has(s.estado);
  const puedeDarDeBaja = esMesaDeControl && ESTADOS_BAJA_POR_MESA_CONTROL.has(s.estado);

  const abrirCancelar = async () => {
    const ok = await confirm({
      title: `Cancelar solicitud ${s.folio}`,
      message: (
        <>
          ¿Seguro que deseas cancelar esta solicitud? Esta acción es <b>terminal</b>.
        </>
      ),
      confirmLabel: "Cancelar solicitud",
      cancelLabel: "Volver",
      confirmColor: "peligro",
    });
    if (ok) cancelar.mutate([]);
  };

  return (
    <Tr
      style={{
        // Realce neutro de "rechazo pendiente" · gris adaptable al esquema
        // (claro: gris tenue · oscuro: panel un poco más claro que el fondo)
        // en vez de un tinte naranja que se veía café sobre el modo oscuro.
        // El punto naranja sigue marcando la atención con la marca.
        background: s.tieneRechazoPendiente ? "light-dark(var(--color-slate-100), var(--color-ink-800))" : undefined,
      }}
    >
      <Td>
        {/* Sin punto de "rechazo pendiente" junto al folio (lo pidió el
            usuario): el realce ya lo dan el fondo gris de la fila y el punto
            semántico de la columna Estado. Padding por defecto (igual que las
            demás celdas) para que la separación entre columnas sea uniforme. */}
        <Text size="sm" fw={500} className="whitespace-nowrap">
          {s.folio}
        </Text>
      </Td>
      <Td>
        {/* Estado como PUNTO + texto plano (alternativa A elegida por el
            usuario · estilo Linear/GitHub) en vez de pill relleno: 15 barras
            sólidas idénticas se leían como una pared azul. El punto carga el
            color por estado de la FUENTE ÚNICA compartida (`tonoDeEstado` en
            `lib/estado-color.ts` · misma paleta que "Mis últimas solicitudes"
            y el donut: verde=autorización, azul=en proceso, índigo=autorizada,
            rojo=rechazos, ámbar=SAT, morado=armado, slate=captura/terminales);
            el texto usa el foreground normal. Se conservan el label compacto
            con nivel n/m, el truncado y el nombre completo en el tooltip. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex min-w-0 items-center gap-2">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: TONO_HEX[tonoDeEstado(s.estado)] }}
              />
              <Text size="xs" fw={600} truncate className="tracking-wide">
                {etiquetaEstadoCompacta(s)}
              </Text>
            </div>
          </TooltipTrigger>
          <TooltipContent>{tooltipEstado(s)}</TooltipContent>
        </Tooltip>
      </Td>
      <Td>
        <TruncatedTooltip label={etiquetaTipo(s.tipo)} disabled={!s.tipo}>
          <Stack gap={0} className="min-w-0">
            <Text size="sm" fw={600} truncate>
              {etiquetaTipo(s.tipo)}
            </Text>
            <Text size="xs" c="dimmed" truncate>
              {etiquetaSubtipo(s.subtipo)}
            </Text>
          </Stack>
        </TruncatedTooltip>
      </Td>
      <Td>
        <TruncatedTooltip label={s.organizacionNombre ?? "—"} disabled={!s.organizacionNombre || s.organizacionNombre.length < 22}>
          <Text size="sm" fw={600} truncate>
            {s.organizacionNombre ?? "—"}
          </Text>
        </TruncatedTooltip>
      </Td>
      <Td>
        <TruncatedTooltip label={s.areaNombre ?? "—"} disabled={!s.areaNombre || s.areaNombre.length < 20}>
          <Text size="sm" truncate>
            {s.areaNombre ?? "—"}
          </Text>
        </TruncatedTooltip>
      </Td>
      {/* Proveedor fusionado con su RFC (2ª línea) · mismo patrón que la
          tabla de Mis facturas · libera una columna completa. */}
      <Td>
        <TruncatedTooltip
          label={`${s.proveedorRazonSocial ?? "—"}${s.proveedorRfc ? ` · ${s.proveedorRfc}` : ""}`}
          disabled={!s.proveedorRazonSocial}
        >
          <Stack gap={0} className="min-w-0">
            <Text size="sm" fw={600} truncate>
              {s.proveedorRazonSocial ?? "—"}
            </Text>
            {s.proveedorRfc && (
              <Text size="xs" c="dimmed" truncate>
                {s.proveedorRfc}
              </Text>
            )}
          </Stack>
        </TruncatedTooltip>
      </Td>
      <Td>
        {s.descripcionPrimerConcepto ? (
          <TruncatedTooltip label={s.descripcionPrimerConcepto} disabled={s.descripcionPrimerConcepto.length < 28}>
            <Text size="sm" truncate>
              {s.descripcionPrimerConcepto}
            </Text>
          </TruncatedTooltip>
        ) : (
          <Text size="xs" c="dimmed">
            —
          </Text>
        )}
      </Td>
      {/* Total con la MONEDA fusionada (código ISO como 2ª línea) · antes eran
          dos columnas · `narrowSymbol` rinde "$" en MXN/USD y el símbolo real
          en otras divisas, y el código ISO abajo desambigua ($ MXN vs $ USD). */}
      <Td align="right">
        {s.totalCfdi !== null ? (
          <Stack gap={0} className="items-end">
            <Text size="sm" fw={700} className="whitespace-nowrap">
              {formatearMonto(s.totalCfdi, s.monedaCfdi, { minimumFractionDigits: 2, currencyDisplay: "narrowSymbol" })}
            </Text>
            {s.monedaCfdi && (
              <Text size="xs" c="dimmed" className="leading-tight">
                {s.monedaCfdi}
              </Text>
            )}
          </Stack>
        ) : (
          <Text size="xs" c="dimmed">
            —
          </Text>
        )}
      </Td>
      <Td>
        {s.fechaPago ? (
          <Text size="sm" fw={500}>
            {formatFecha(s.fechaPago)}
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            —
          </Text>
        )}
      </Td>
      {/* HOTSPOT-DOMINIO: la columna "Núm. Doc" (número de documento asignado a
          la solicitud) se RETIRÓ de la tabla porque aún no existe fuente del
          dato — era una celda permanentemente vacía. Cuando el dominio lo
          defina, reintroducirla aquí y en `columnas()` de SolicitudesTable. */}
      <Td>
        <Text size="sm">{formatFecha(s.fechaInicio)}</Text>
      </Td>
      {/* Acciones (revisión UX) · UNA acción primaria visible (continuar si el
          wizard sigue abierto, si no ver detalle) + menú "⋯" para el resto.
          Las destructivas (cancelar/dar de baja) viven SIEMPRE dentro del
          menú — antes el botón rojo de cancelar quedaba a un click directo
          junto al de abrir, con riesgo de click accidental. */}
      <Td align="right">
        <Group gap={4} justify="flex-end" wrap="nowrap">
          {/* Jerarquía sólida (iterada con el usuario): SOLO la acción
              principal va rellena con ícono blanco, en TINTA OSCURA (ink-800,
              botón neutro elegante que deja el naranja del header como única
              voz de marca) y el menú ⋯ discreto en su variante ligera. Par
              claro/oscuro explícito: en dark la card ya ES ink-800, así que
              el botón sube a ink-600 para no fundirse con el fondo. */}
          {urlContinuar ? (
            <IconAction
              label="Continuar solicitud"
              size="sm"
              variant="filled"
              className="bg-ink-700 hover:bg-ink-800 dark:bg-ink-600 dark:hover:bg-ink-500"
              onClick={() => navigate({ to: urlContinuar as never })}
            >
              {/* Variante FILLED del play (pedido del usuario): triángulo
                  blanco sólido, no contorneado. */}
              <IconPlayerPlayFilled size={16} />
            </IconAction>
          ) : (
            <IconAction
              label="Ver detalle"
              size="sm"
              variant="filled"
              className="bg-ink-700 hover:bg-ink-800 dark:bg-ink-600 dark:hover:bg-ink-500"
              onClick={irADetalle}
            >
              <IconExternalLink size={16} stroke={1.8} />
            </IconAction>
          )}
          {(urlContinuar || puedeCancelar || puedeDarDeBaja) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                {/* Secundario OUTLINE (opción C, iterada con el usuario tras
                    la D): sin relleno — borde gris con el glifo en la misma
                    tinta del principal; hover con fondo tenue (variante
                    default del átomo). Par oscuro: borde/glifo suben de tono
                    para leerse sobre la card ink-800. */}
                <IconButton
                  aria-label={`Más acciones de ${s.folio}`}
                  size="sm"
                  variant="default"
                  className="border-ink-300 text-ink-700 dark:border-ink-500 dark:text-ink-200"
                >
                  <IconDots size={16} stroke={1.8} />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {urlContinuar && (
                  <DropdownMenuItem onClick={irADetalle}>
                    <IconExternalLink size={14} stroke={1.8} />
                    Ver detalle
                  </DropdownMenuItem>
                )}
                {puedeCancelar && (
                  <DropdownMenuItem destructive onClick={() => void abrirCancelar()}>
                    <IconSquareRoundedX size={14} stroke={1.8} />
                    Cancelar solicitud
                  </DropdownMenuItem>
                )}
                {puedeDarDeBaja && (
                  <DropdownMenuItem destructive onClick={() => setBajaOpen(true)}>
                    <IconBan size={14} stroke={1.8} />
                    Dar de baja (Mesa de Control)
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </Group>
      </Td>
      <Dialog open={bajaOpen} onOpenChange={setBajaOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Dar de baja solicitud {s.folio}</DialogTitle>
          </DialogHeader>
          <Stack gap="md">
            {s.enviadaAContabilizacion ? (
              <Alert variant="warning" icon={<IconAlertTriangle size={16} />}>
                La póliza de esta solicitud ya se generó y el archivo ya se depositó en SFTP. Dar de baja NO revierte ese depósito.
              </Alert>
            ) : null}
            <MotivosForm variante="baja" loading={baja.isPending} onCancelar={() => setBajaOpen(false)} onConfirmar={(motivos) => baja.mutate(motivos)} />
          </Stack>
        </DialogContent>
      </Dialog>
    </Tr>
  );
}

function TruncatedTooltip({ label, disabled, children }: { label: string; disabled?: boolean; children: React.ReactNode }) {
  if (disabled) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="max-w-80">{label}</TooltipContent>
    </Tooltip>
  );
}
